import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, CheckCircle2, ChevronRight, ChevronLeft, Loader2,
  XCircle, Trophy, Target, HelpCircle, RotateCcw,
  Heart, Shield, Car, Briefcase, IndianRupee, FileCheck, Users,
  AlertTriangle, Lightbulb, BookOpen, Star, Zap
} from "lucide-react";
import { authGet, authPost, isAuthenticated } from "../utils/auth";
import { toast } from "sonner";

// Pick icon based on card title keywords
const CARD_ICONS = [
  { keywords: ["premium", "pay", "cost", "price", "amount"], icon: IndianRupee, color: "text-green-600", bg: "bg-green-500/10", border: "border-green-500/30" },
  { keywords: ["health", "hospital", "medical", "doctor", "illness"], icon: Heart, color: "text-rose-600", bg: "bg-rose-500/10", border: "border-rose-500/30" },
  { keywords: ["life", "death", "family", "protect", "nominee"], icon: Shield, color: "text-[#2E3B57]", bg: "bg-[#EDF1F8]/30", border: "border-blue-500/30" },
  { keywords: ["vehicle", "car", "bike", "motor", "accident", "damage"], icon: Car, color: "text-orange-600", bg: "bg-orange-500/10", border: "border-orange-500/30" },
  { keywords: ["type", "kind", "category", "option"], icon: Briefcase, color: "text-purple-600", bg: "bg-purple-500/10", border: "border-purple-500/30" },
  { keywords: ["claim", "process", "step", "how", "submit", "file"], icon: FileCheck, color: "text-cyan-600", bg: "bg-cyan-500/10", border: "border-cyan-500/30" },
  { keywords: ["people", "insured", "policyholder", "company", "insurer", "who"], icon: Users, color: "text-indigo-600", bg: "bg-indigo-500/10", border: "border-indigo-500/30" },
  { keywords: ["risk", "danger", "loss", "problem", "why"], icon: AlertTriangle, color: "text-yellow-600", bg: "bg-yellow-500/10", border: "border-yellow-500/30" },
  { keywords: ["remember", "key", "important", "summary", "takeaway", "recap"], icon: Star, color: "text-[#A87608]", bg: "bg-[#D4A017]/10", border: "border-[#D4A017]/30" },
  { keywords: ["benefit", "advantage", "good", "feature"], icon: Zap, color: "text-emerald-600", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  { keywords: ["what", "definition", "meaning", "basic"], icon: Lightbulb, color: "text-[#A87608]", bg: "bg-[#D4A017]/10", border: "border-[#D4A017]/30" },
];

function getCardStyle(title) {
  const lower = title.toLowerCase();
  for (const entry of CARD_ICONS) {
    if (entry.keywords.some(kw => lower.includes(kw))) return entry;
  }
  return { icon: BookOpen, color: "text-[#A87608]", bg: "bg-[#D4A017]/10", border: "border-[#D4A017]/30" };
}

function renderStoryContent(content) {
  const lines = content.split("\n");
  const elements = [];
  
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (!trimmed) { elements.push(<div key={i} className="h-2" />); continue; }
    
    // Concept Card: [CARD: title | point1 | point2 | point3]
    const cardMatch = trimmed.match(/^\[CARD:\s*(.+?)\]$/i);
    if (cardMatch) {
      const parts = cardMatch[1].split("|").map(p => p.trim());
      const title = parts[0];
      const bullets = parts.slice(1);
      const style = getCardStyle(title);
      const IconComp = style.icon;
      
      elements.push(
        <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`my-4 rounded-xl border ${style.border} ${style.bg} p-4`}>
          <div className="flex items-center gap-2.5 mb-3">
            <div className={`w-8 h-8 rounded-lg ${style.bg} flex items-center justify-center`}>
              <IconComp className={`h-4 w-4 ${style.color}`} />
            </div>
            <h4 className={`font-semibold text-sm ${style.color}`}>{title}</h4>
          </div>
          <div className="space-y-2 pl-1">
            {bullets.map((b, j) => (
              <div key={j} className="flex items-start gap-2">
                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${style.color.replace('text-', 'bg-')}`} />
                <p className="text-slate-800 text-sm leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </motion.div>
      );
      continue;
    }
    
    // Skip old SCENE markers gracefully
    if (/^\[SCENE:/i.test(trimmed)) continue;
    
    // Emoji section headers
    if (/^[^\w\s]/.test(trimmed) && trimmed.length < 80 && !trimmed.startsWith("-")) {
      elements.push(<h3 key={i} className="text-[#A87608] font-semibold text-base mt-5 mb-2">{trimmed}</h3>);
      continue;
    }
    
    // Bullet points
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      elements.push(
        <div key={i} className="flex gap-2 ml-1 mb-1">
          <span className="text-[#A87608] mt-0.5 shrink-0 text-xs">-</span>
          <p className="text-[#2E3B57] text-sm leading-relaxed">{trimmed.substring(2)}</p>
        </div>
      );
      continue;
    }
    
    // Dialogue (character speaking)
    if (/^(Arjun|Sharma|Priya|Ramesh|Sir|Bhai|Uncle|Aunty)[\s]*[:(]/i.test(trimmed)) {
      const colonIdx = trimmed.indexOf(":");
      if (colonIdx > 0) {
        const speaker = trimmed.substring(0, colonIdx).trim();
        const dialogue = trimmed.substring(colonIdx + 1).trim();
        elements.push(
          <div key={i} className="mb-2 pl-3 border-l-2 border-[#C9D4E6]/60">
            <span className="text-[#A87608]/80 text-xs font-medium">{speaker}</span>
            <p className="text-slate-800 text-sm leading-relaxed">{dialogue}</p>
          </div>
        );
        continue;
      }
    }
    
    // Regular text
    elements.push(<p key={i} className="text-[#2E3B57] text-sm leading-relaxed mb-1.5">{lines[i]}</p>);
  }
  
  return elements;
}

export default function TrainingChapter() {
  const { moduleId, chapterId } = useParams();
  const navigate = useNavigate();
  const [chapter, setChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [allChapters, setAllChapters] = useState([]);
  const [currentModule, setCurrentModule] = useState(null);
  // Chapter Quiz state
  const [phase, setPhase] = useState("reading"); // reading | quiz | result
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizLoading, setQuizLoading] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) { navigate("/advisor/login"); return; }
    fetchData();
    // Reset quiz state on chapter change
    setPhase("reading");
    setQuizQuestions([]);
    setQuizResult(null);
    setCurrentQ(0);
    setAnswers([]);
  }, [chapterId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [chapterRes, modulesRes] = await Promise.all([
        authGet(`/api/training/chapter/${chapterId}`),
        authGet("/api/training/modules")
      ]);
      if (chapterRes.ok) setChapter(chapterRes.data.chapter);
      if (modulesRes.ok) {
        const mod = (modulesRes.data.modules || []).find(m => m.id === moduleId);
        if (mod) {
          setCurrentModule(mod);
          setAllChapters(mod.chapters);
          const ch = mod.chapters.find(c => c.id === chapterId);
          if (ch) setCompleted(ch.completed);
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const markComplete = async () => {
    setMarking(true);
    try {
      const res = await authPost(`/api/training/chapter/${chapterId}/complete`);
      if (res.ok) {
        setCompleted(true);
        toast.success("Chapter completed! Take the quiz now.");
        startQuiz();
      }
    } catch (e) { toast.error("Failed to mark complete"); }
    setMarking(false);
  };

  const startQuiz = async () => {
    setQuizLoading(true);
    setPhase("quiz");
    try {
      const res = await authGet(`/api/training/chapter-quiz/${chapterId}`);
      if (res.ok && res.data.questions) {
        setQuizQuestions(res.data.questions);
        setAnswers(new Array(res.data.questions.length).fill(-1));
        setCurrentQ(0);
      } else {
        toast.error("Failed to load quiz");
        setPhase("reading");
      }
    } catch (e) {
      toast.error("Failed to load quiz");
      setPhase("reading");
    }
    setQuizLoading(false);
  };

  const selectAnswer = (optIdx) => {
    const newAnswers = [...answers];
    newAnswers[currentQ] = optIdx;
    setAnswers(newAnswers);
  };

  const submitQuiz = async () => {
    const unanswered = answers.filter(a => a === -1).length;
    if (unanswered > 0) {
      toast.error(`Answer all questions (${unanswered} remaining)`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await authPost(`/api/training/chapter-quiz/${chapterId}/submit`, { answers });
      if (res.ok) {
        setQuizResult(res.data);
        setPhase("result");
      }
    } catch (e) { toast.error("Submit failed"); }
    setSubmitting(false);
  };

  const currentIdx = allChapters.findIndex(c => c.id === chapterId);
  const nextChapter = currentIdx >= 0 && currentIdx < allChapters.length - 1 ? allChapters[currentIdx + 1] : null;
  const prevChapter = currentIdx > 0 ? allChapters[currentIdx - 1] : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] flex flex-col items-center justify-center gap-3">
        <Loader2 className="h-8 w-8 text-[#A87608] animate-spin" />
        <p className="text-[#3A4557] text-sm">Generating chapter content with AI...</p>
      </div>
    );
  }

  if (!chapter) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8]">
      <div className="max-w-3xl mx-auto px-4 py-6 pb-32">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(`/advisor/training/${moduleId}`)}
            className="text-[#3A4557] hover:text-[#1B2A4A] transition-colors" data-testid="back-to-module">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <p className="text-xs text-[#3A4557]">
              {currentModule?.title} — Chapter {currentIdx + 1} of {allChapters.length}
            </p>
            <h1 className="text-lg font-bold text-[#1B2A4A] truncate">{chapter.title}</h1>
          </div>
        </div>

        {/* Reading Progress Indicator */}
        <div className="flex gap-1 mb-6">
          {allChapters.map((ch) => (
            <div key={ch.id}
              className={`h-1 flex-1 rounded-full ${
                ch.id === chapterId ? "bg-[#D4A017]" : ch.completed ? "bg-emerald-500" : "bg-[#E2E9F4]"
              }`} />
          ))}
        </div>

        {/* Phase: Reading */}
        {phase === "reading" && (
          <>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="bg-[#EDF1F8]/40 border border-[#C9D4E6]/50 rounded-2xl p-6 mb-6"
              data-testid="chapter-content">
              <div className="prose prose-invert max-w-none">
                {renderStoryContent(chapter.content)}
              </div>
            </motion.div>

            {/* Actions */}
            <div className="space-y-3">
              {!completed ? (
                <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                  onClick={markComplete} disabled={marking}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-medium py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                  data-testid="mark-complete-btn">
                  {marking ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
                  {marking ? "Completing..." : "Complete & Take Quiz"}
                </motion.button>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-2 py-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <span className="text-emerald-600 font-medium text-sm">Chapter Completed</span>
                  </div>
                  <button onClick={startQuiz}
                    className="w-full py-3 bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl text-[#A87608] hover:bg-[#D4A017]/15 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    data-testid="take-chapter-quiz-btn">
                    <HelpCircle className="h-4 w-4" /> Take Chapter Quiz
                  </button>
                </>
              )}

              <div className="flex gap-3">
                {prevChapter && (
                  <button onClick={() => navigate(`/advisor/training/${moduleId}/chapter/${prevChapter.id}`)}
                    className="flex-1 py-3 bg-[#EDF1F8]/50 border border-[#C9D4E6]/50 rounded-xl text-[#3A4557] hover:text-[#1B2A4A] text-sm font-medium transition-colors"
                    data-testid="prev-chapter-btn">Previous</button>
                )}
                {nextChapter ? (
                  <button onClick={() => navigate(`/advisor/training/${moduleId}/chapter/${nextChapter.id}`)}
                    className="flex-1 py-3 bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl text-[#A87608] hover:bg-[#D4A017]/15 text-sm font-medium transition-colors flex items-center justify-center gap-1"
                    data-testid="next-chapter-btn">
                    Next Chapter <ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button onClick={() => navigate(`/advisor/training/${moduleId}`)}
                    className="flex-1 py-3 bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl text-[#A87608] hover:bg-[#D4A017]/15 text-sm font-medium transition-colors"
                    data-testid="back-to-module-btn">Back to Module</button>
                )}
              </div>
            </div>
          </>
        )}

        {/* Phase: Chapter Quiz */}
        {phase === "quiz" && (
          <ChapterQuizView
            questions={quizQuestions}
            loading={quizLoading}
            currentQ={currentQ}
            setCurrentQ={setCurrentQ}
            answers={answers}
            selectAnswer={selectAnswer}
            submitQuiz={submitQuiz}
            submitting={submitting}
            chapterTitle={chapter.title}
          />
        )}

        {/* Phase: Quiz Result */}
        {phase === "result" && quizResult && (
          <ChapterQuizResult
            result={quizResult}
            chapterTitle={chapter.title}
            onRetake={() => { setPhase("quiz"); setQuizResult(null); setAnswers(new Array(quizQuestions.length).fill(-1)); setCurrentQ(0); }}
            onReview={() => { setPhase("quiz"); }}
            onContinue={() => {
              if (nextChapter) navigate(`/advisor/training/${moduleId}/chapter/${nextChapter.id}`);
              else navigate(`/advisor/training/${moduleId}`);
            }}
            hasNext={!!nextChapter}
          />
        )}
      </div>
    </div>
  );
}

/* ============ CHAPTER QUIZ VIEW ============ */
function ChapterQuizView({ questions, loading, currentQ, setCurrentQ, answers, selectAnswer, submitQuiz, submitting, chapterTitle }) {
  if (loading || questions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Loader2 className="h-8 w-8 text-[#A87608] animate-spin" />
        <p className="text-[#3A4557] text-sm">Generating chapter quiz...</p>
      </div>
    );
  }

  const q = questions[currentQ];
  const answeredCount = answers.filter(a => a !== -1).length;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      {/* Quiz Header */}
      <div className="bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl p-3 mb-4 flex items-center gap-3">
        <HelpCircle className="h-5 w-5 text-[#A87608] shrink-0" />
        <div>
          <p className="text-[#A87608] font-medium text-sm">Chapter Quiz</p>
          <p className="text-[#3A4557] text-xs">{chapterTitle} — 5 questions</p>
        </div>
      </div>

      {/* Progress Dots */}
      <div className="flex gap-1.5 mb-5" data-testid="chapter-quiz-dots">
        {questions.map((_, i) => (
          <button key={i} onClick={() => setCurrentQ(i)}
            className={`h-2 flex-1 rounded-full transition-all ${
              i === currentQ ? "bg-[#D4A017]" : answers[i] !== -1 ? "bg-emerald-500" : "bg-[#E2E9F4]"
            }`} />
        ))}
      </div>

      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div key={currentQ}
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.15 }}
          className="bg-[#EDF1F8]/40 border border-[#C9D4E6]/50 rounded-2xl p-5 mb-5"
          data-testid="chapter-quiz-card">
          <span className="text-xs bg-[#D4A017]/15 text-[#A87608] px-2 py-0.5 rounded-full font-medium">
            Q{currentQ + 1}/{questions.length}
          </span>
          <p className="text-[#1B2A4A] font-medium mt-3 mb-4">{q.question}</p>

          <div className="space-y-2.5">
            {q.options.map((opt, optIdx) => {
              const isSelected = answers[currentQ] === optIdx;
              return (
                <button key={optIdx} onClick={() => selectAnswer(optIdx)}
                  className={`w-full text-left p-3.5 rounded-xl border transition-all ${
                    isSelected
                      ? "bg-[#D4A017]/10 border-[#D4A017]/40 text-[#A87608]"
                      : "bg-white/50 border-[#C9D4E6]/50 text-[#2E3B57] hover:border-[#C9D4E6]"
                  }`}
                  data-testid={`ch-quiz-opt-${optIdx}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      isSelected ? "border-[#D4A017] bg-[#D4A017]/15" : "border-[#C9D4E6]"
                    }`}>
                      {isSelected && <div className="w-2 h-2 rounded-full bg-[#D4A017]" />}
                    </div>
                    <span className="text-sm">{opt}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex gap-3">
        <button onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0}
          className="flex-1 py-3 bg-[#EDF1F8]/50 border border-[#C9D4E6]/50 rounded-xl text-[#3A4557] hover:text-[#1B2A4A] disabled:opacity-30 text-sm font-medium transition-colors flex items-center justify-center gap-1"
          data-testid="ch-quiz-prev">
          <ChevronLeft className="h-4 w-4" /> Prev
        </button>
        {currentQ < questions.length - 1 ? (
          <button onClick={() => setCurrentQ(currentQ + 1)}
            className="flex-1 py-3 bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl text-[#A87608] hover:bg-[#D4A017]/15 text-sm font-medium transition-colors flex items-center justify-center gap-1"
            data-testid="ch-quiz-next">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button onClick={submitQuiz} disabled={submitting}
            className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-1"
            data-testid="ch-quiz-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {submitting ? "Submitting..." : `Submit (${answeredCount}/${questions.length})`}
          </button>
        )}
      </div>
    </motion.div>
  );
}

/* ============ CHAPTER QUIZ RESULT ============ */
function ChapterQuizResult({ result, chapterTitle, onRetake, onReview, onContinue, hasNext }) {
  return (
    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", duration: 0.4 }}
      className="max-w-md mx-auto">
      <div className={`rounded-2xl border p-6 text-center ${
        result.passed
          ? "bg-gradient-to-b from-emerald-500/10 to-[#EDF1F8] border-emerald-500/30"
          : "bg-gradient-to-b from-[#D4A017]/10 to-[#EDF1F8] border-[#D4A017]/30"
      }`} data-testid="chapter-quiz-result">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: "spring" }}
          className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-3 ${
            result.passed ? "bg-emerald-500/20" : "bg-[#D4A017]/15"
          }`}>
          {result.passed ? <Trophy className="h-8 w-8 text-emerald-600" /> : <Target className="h-8 w-8 text-[#A87608]" />}
        </motion.div>

        <h2 className={`text-xl font-bold mb-1 ${result.passed ? "text-emerald-600" : "text-[#A87608]"}`}>
          {result.passed ? "Great Job!" : "Keep Trying!"}
        </h2>
        <p className="text-[#3A4557] text-xs mb-4">{chapterTitle}</p>

        <div className="bg-[#EDF1F8]/50 rounded-xl p-4 mb-5">
          <p className="text-3xl font-bold text-[#1B2A4A] mb-0.5">{result.score}%</p>
          <p className="text-sm text-[#3A4557]">{result.correct_count}/{result.total_questions} correct</p>
          <p className={`text-xs mt-1 font-medium ${result.passed ? "text-emerald-600" : "text-[#A87608]"}`}>
            {result.passed ? "Passed! You can proceed." : "Need 60% to pass. Review and retry!"}
          </p>
        </div>

        <div className="space-y-2.5">
          {result.passed ? (
            <button onClick={onContinue}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-1"
              data-testid="ch-quiz-continue">
              {hasNext ? "Next Chapter" : "Back to Module"} <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={onRetake}
              className="w-full py-3 bg-[#D4A017]/10 border border-[#D4A017]/30 text-[#A87608] rounded-xl text-sm font-medium hover:bg-[#D4A017]/15 transition-colors flex items-center justify-center gap-2"
              data-testid="ch-quiz-retake">
              <RotateCcw className="h-4 w-4" /> Retake Quiz
            </button>
          )}
          <button onClick={onReview}
            className="w-full py-2.5 text-[#3A4557] text-sm hover:text-[#1B2A4A] transition-colors"
            data-testid="ch-quiz-review">
            Review Answers
          </button>
        </div>
      </div>
    </motion.div>
  );
}
